/**
 * API Configuration
 * Handles environment-based API URL selection
 */

const PRODUCTION_API_URL = import.meta.env.VITE_API_PRODUCTION_URL || 'https://sue-sate-dev-api-g6beexd6ethtcjbn.westeurope-01.azurewebsites.net/';
const LOCAL_API_URL = import.meta.env.VITE_API_BASE_URL || 'http://127.0.0.1:8000';
const LOCAL_API_STORAGE_KEY = 'useLocalAPI';

/**
 * Checks if the current environment is development
 * @returns {boolean} True if running on localhost
 */
export function isDevelopmentMode() {
  const hostname = window.location.hostname;
  return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '';
}

/**
 * Gets the API base URL based on environment and user preference
 * - Production: Always returns production URL
 * - Development: Checks localStorage for user preference, defaults to local
 * @returns {string} The API base URL
 */
export function getApiBaseUrl() {
  // In production, always use production API
  if (!isDevelopmentMode()) {
    return PRODUCTION_API_URL;
  }

  // In development, check user preference
  const useLocal = localStorage.getItem(LOCAL_API_STORAGE_KEY) === 'true';
  return useLocal ? LOCAL_API_URL : PRODUCTION_API_URL;
}

/**
 * Sets the user's preference for local API (development only)
 * @param {boolean} useLocal - Whether to use local API
 */
export function setUseLocalAPI(useLocal) {
  if (!isDevelopmentMode()) {
    console.warn('Cannot set local API in production mode');
    return;
  }
  localStorage.setItem(LOCAL_API_STORAGE_KEY, useLocal ? 'true' : 'false');
}

/**
 * Gets the current preference for local API
 * @returns {boolean} True if using local API
 */
export function getUseLocalAPI() {
  if (!isDevelopmentMode()) {
    return false;
  }
  return localStorage.getItem(LOCAL_API_STORAGE_KEY) === 'true';
}

/**
 * Gets the current API mode for display purposes (user-facing; avoids "Local"/localhost in demos)
 * @returns {string} "Pilot" (local/dev) or "Production"
 */
export function getApiMode() {
  const baseUrl = getApiBaseUrl();
  return baseUrl === LOCAL_API_URL ? 'Pilot' : 'Production';
}

// Log current API configuration in development
if (isDevelopmentMode()) {
  console.log(`🔧 API Config: Using ${getApiMode()} API (${getApiBaseUrl()})`);
}
